import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

const ImageComparison = ({ beforeImage, afterImage, initialPosition = 50 }) => {
  const [sliderPosition, setSliderPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;

    // Constrain between 0 and 100
    const bounded = Math.min(Math.max(percentage, 0), 100);
    setSliderPosition(bounded);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    handleMove(e.touches[0].clientX);
  };

  const handleContainerClick = (e) => {
    handleMove(e.clientX);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, sliderPosition]);

  return (
    <Box
      ref={containerRef}
      onClick={handleContainerClick}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 10',
        overflow: 'hidden',
        cursor: 'ew-resize',
        userSelect: 'none',
        touchAction: 'pan-y',
        backgroundColor: '#f0f0f0'
      }}>
      {/* Before Image (Right side - full width) */}
      <Box
        component='img'
        src={beforeImage}
        alt='Before'
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          pointerEvents: 'none'
        }}
      />

      {/* After Image (Left side - clipped) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`,
          willChange: 'clip-path'
        }}>
        <Box
          component='img'
          src={afterImage}
          alt='After'
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            pointerEvents: 'none'
          }}
        />
      </Box>

      {/* Slider Line and Handle */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: `${sliderPosition}%`,
          transform: 'translateX(-50%)',
          width: '4px',
          height: '100%',
          backgroundColor: '#fff',
          boxShadow: '0 0 10px rgba(0,0,0,0.5)',
          cursor: 'ew-resize',
          zIndex: 10,
          willChange: 'left'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}>
        {/* Handle Circle */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '50px',
            height: '50px',
            backgroundColor: '#fff',
            borderRadius: '50%',
            boxShadow: '0 0 15px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'ew-resize',
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'translate(-50%, -50%) scale(1.1)'
            },
            '&:active': {
              transform: 'translate(-50%, -50%) scale(0.95)'
            }
          }}>
          {/* Left Arrow */}
          <Box
            sx={{
              position: 'absolute',
              left: '12px',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '8px solid #333'
            }}
          />
          {/* Right Arrow */}
          <Box
            sx={{
              position: 'absolute',
              right: '12px',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderLeft: '8px solid #333'
            }}
          />
        </Box>
      </Box>

      {/* Labels (Optional) */}
      <Box
        sx={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontWeight: 600,
          pointerEvents: 'none',
          opacity: sliderPosition > 15 ? 1 : 0,
          transition: 'opacity 0.3s ease'
        }}>
        VORHER
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '4px',
          fontSize: '0.875rem',
          fontWeight: 600,
          pointerEvents: 'none',
          opacity: sliderPosition < 85 ? 1 : 0,
          transition: 'opacity 0.3s ease'
        }}>
        NACHHER
      </Box>
    </Box>
  );
};

export default ImageComparison;
